import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import RegisterErrorCardHeaderTitle from 'frontend-login/components/register-error/card-header-title';
import RegisterErrorTokenExpiredEmailAlreadySent from 'frontend-login/components/register-error/token-expired/email-already-sent';
import ENV from 'frontend-login/config/environment';
import fetch, { NetworkError } from 'frontend-login/utils/fetch';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';

export class EmailAlreadySentError extends Error {
  name = this.constructor.name;
}

export default class RegisterErrorTokenExpired extends Component {
  @service declare router: Services['router'];

  @tracked error: unknown = null;
  @tracked isLoading = false;

  handleTokenExpiredAction = async (): Promise<void> => {
    this.isLoading = true;

    const url = new URL(
      '/apigateway/v1/app/resend-password-reset',
      getHostDomain({ frontendGateway: ENV.frontendGateway }),
    );

    const body = JSON.stringify({
      password_token: this.router.currentRoute!.queryParams?.['token'],
      password_uidb64: this.router.currentRoute!.queryParams?.['uidb64'],
    });

    try {
      await fetch(url, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error: unknown) {
      this.isLoading = false;

      if (!(error instanceof NetworkError)) {
        throw error;
      }

      if (error.cause.response.status === 425) {
        this.error = new EmailAlreadySentError();
        return;
      }

      throw error;
    }

    this.isLoading = false;

    this.router.replaceWith('register-email-sent');
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get emailAlreadySent() {
    return this.error instanceof EmailAlreadySentError;
  }

  <template>
    {{#if this.emailAlreadySent}}
      <RegisterErrorTokenExpiredEmailAlreadySent />
    {{else}}
      <PrismCard>
        <:header as |Header|>
          <Header.title>
            <RegisterErrorCardHeaderTitle />
          </Header.title>
        </:header>
        <:content>
          <PrismMessage
            @description={{t "password.error.token_expired.description"}}
            @illustration="100x100_tangled"
            @title={{t "general.error_title"}}
          />
        </:content>
        <:footer>
          <div class="flex justify-end">
            <PrismButton
              @buttonType="secondary"
              @isDisabled={{this.isLoading}}
              @isLoading={{this.isLoading}}
              @label={{t "password.error.token_expired.action"}}
              @onClick={{this.handleTokenExpiredAction}}
            />
          </div>
        </:footer>
      </PrismCard>
    {{/if}}
  </template>
}
